import { rolesApi } from '@/features/roles';
import type { RoleOption } from '@/features/roles';
import { API_ENDPOINTS } from '@/constants/api';
import { useUsers } from '@/features/users/hooks/useUsers';
import type { UserFilters } from '@/features/users/store/entitySlice';
import type { EntityListColumn } from '@/shared/components/EntityList';
import { EntityList } from '@/shared/components/EntityList';
import { PageLayout } from '@/shared/components/PageLayout';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import type { User } from '@/shared/types/user';
import { formatDate } from '@/shared/utils/date';
import { CBadge } from '@coreui/react';
import React, { useEffect, useState } from 'react';

export const UsersList: React.FC = React.memo(() => {
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [rolesError, setRolesError] = useState('');
  useEffect(() => {
    let active = true;
    void rolesApi
      .getOptions()
      .then((options) => {
        if (active) setRoles(options);
      })
      .catch(() => {
        if (active) setRolesError('Failed to load roles.');
      });
    return () => {
      active = false;
    };
  }, []);

  const {
    users,
    error,
    currentPage,
    perPage,
    lastPage,
    total,
    filters,
    handleFiltersChange,
    handlePageChange,
    handlePerPageChange,
    handleDelete,
    handleClearFilters,
  } = useUsers();

  const columns: EntityListColumn<User>[] = [
    {
      header: 'Name',
      key: 'firstName',
      render: (user: User) => `${user.firstName} ${user.lastName}`,
    },
    {
      header: 'Email',
      key: 'email',
    },
    {
      header: 'Role',
      key: 'roles',
      render: (user: User) => user.roles?.[0]?.name || '-',
    },
    {
      header: 'Status',
      key: 'status',
      render: (user: User) => (
        <CBadge color={user.status ? 'success' : 'danger'}>
          {user.status ? 'Active' : 'Inactive'}
        </CBadge>
      ),
    },
    {
      header: 'Last Modified',
      key: 'lastModifiedBy',
      render: (user: User) => {
        if (!user.lastModifiedBy) return '-';
        return (
          <div className="last-modified-by-container">
            <div>{user.lastModifiedBy.name}</div>
            <small>{user.lastModifiedBy.email}</small>
            {user.lastModifiedBy.timestamp && (
              <div>
                <small>{formatDate(user.lastModifiedBy.timestamp)}</small>
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: 'Actions',
      key: 'actions' as const,
    },
  ];

  const isFiltersEmpty = (filters: UserFilters) => {
    return !filters.search && !filters.role && !filters.status;
  };

  return (
    <PageLayout>
      <EntityList
        title="User Management"
        items={users}
        error={error || rolesError}
        currentPage={currentPage}
        perPage={perPage}
        totalPages={lastPage}
        total={total}
        columns={columns}
        filters={filters}
        filterConfigs={[
          { type: 'search', key: 'search', placeholder: 'Search users...' },
          {
            type: 'select',
            key: 'role',
            options: [
              { value: '', label: 'All Roles' },
              ...roles.map((role) => ({
                value: role.name,
                label: role.name,
              })),
            ],
          },
          {
            type: 'select',
            key: 'status',
            options: [
              { value: '', label: 'All Statuses' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ],
          },
        ]}
        createEndpoint={API_ENDPOINTS.USERS.NEW}
        updateEndpoint={API_ENDPOINTS.USERS.UPDATE}
        onDelete={handleDelete}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        isFiltersEmpty={isFiltersEmpty}
        requiredViewPermission={USER_PERMISSIONS.UsersView}
        requiredManagePermission={USER_PERMISSIONS.UsersEdit}
      />
    </PageLayout>
  );
});
